import dayjs, { Dayjs } from 'dayjs';
import { parseApiUtcDateTimeToLocal } from '@fishven/utils';
import { IDateFormatByLang } from '@app/interfaces/dateFormatByLang';
import { filterDateFormat } from '@app/constants/dateFormats';
import { Language } from '@app/types/Language';

/** Re-exports for gradual migration toward API-aware UTC parsing. */
export { parseApiUtcDateTimeToLocal, parseApiUtcDateTimeToLocalOrNull } from '@fishven/utils';
export { API_DATETIME_FORMAT, formatDateTimeForApiUtc, formatDateTimeForApiUtcOrNull } from '@fishven/utils';

/**
 * License `startDate` / `endDate` and ban period: calendar date only (`YYYY-MM-DD`).
 * Uses the operator session's local calendar (browser); suitable when the API persists SQL DATE.
 */
export function formatLicenseCalendarDateForApi(value: Dayjs | Date | string | null | undefined): string | undefined {
  if (value == null) return undefined;
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : undefined;
}

export const dateItemsNumPad = (num: number) => `${num}`.padStart(2, '0');

export const ensureDateTimeFormat = (format: string): string => {
  if (/[hHkK]/.test(format) && /m/.test(format)) {
    return format;
  }
  if (/[hHkK]/.test(format) && !/m/.test(format)) {
    return format + ':mm';
  }
  return format + ' HH:mm';
};

export const formatUtcToLocal = (dateUtc: string, format: string): string => {
  const safeFormat = ensureDateTimeFormat(format);
  return dayjs.utc(dateUtc).local().format(safeFormat);
};

export const formatDateForApiPathParams = (date: Date, useLocalDate = false) => {
  const now_utc = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );

  const utcDate = useLocalDate ? date : new Date(now_utc);

  if (useLocalDate) {
    return `${utcDate.getFullYear()}-${dateItemsNumPad(utcDate.getMonth() + 1)}-${dateItemsNumPad(
      utcDate.getDate(),
    )} ${dateItemsNumPad(utcDate.getHours())}:${dateItemsNumPad(utcDate.getMinutes())}:${dateItemsNumPad(
      utcDate.getSeconds(),
    )}`;
  }

  return `${utcDate.getUTCFullYear()}-${dateItemsNumPad(utcDate.getUTCMonth() + 1)}-${dateItemsNumPad(
    utcDate.getUTCDate(),
  )} ${dateItemsNumPad(utcDate.getUTCHours())}:${dateItemsNumPad(utcDate.getUTCMinutes())}:${dateItemsNumPad(
    utcDate.getUTCSeconds(),
  )}`;
};

// ---------------------------------------------------------------------------
// Legacy string/date handling (unchanged behaviour — no @fishven/utils)
// ---------------------------------------------------------------------------

export const fromUTCStringToLocalString = (dateUTC: string) => {
  if (!dateUTC) {
    return '';
  }

  const [date, time] = dateUTC.split(' ');
  const splittedDate = date.split('-').map((n, index) => Number.parseInt(n) - (index === 1 ? 1 : 0)) as [
    number,
    number,
    number,
  ];
  const splittedTime = (time?.split(':')?.map((n) => Number.parseInt(n)) as [number, number, number]) || [];

  const params = [...splittedDate, ...splittedTime] as [number, number, number, number, number, number];

  const currentDate = new Date();
  const newDate = new Date(...params);

  newDate.setHours(newDate.getHours() + (currentDate.getTimezoneOffset() * -1) / 60);

  return formatDateForApiPathParams(newDate, true);
};

export const formatDateByLang = (
  dateStr: string,
  locale: string,
  dateFormat: IDateFormatByLang,
  withTime = false,
  dateFormatType: keyof (typeof dateFormat)[Language] = 'long',
  isReversed = false,
) => {
  const params = dateStr.split('-');

  const days = isReversed ? params[2] : params[0];
  const months = isReversed ? params[1] : params[1];
  const year = isReversed ? params[0] : params[2];
  const yearToDisplay = year ? year : new Date().getFullYear() - 1;

  const date = `${days}-${months}-${yearToDisplay}`;

  const targetDateFormat = dateFormat?.[locale]?.[dateFormatType] || 'DD/MM/YYYY';
  const format = withTime ? `${targetDateFormat} HH:mm` : targetDateFormat;

  return dayjs(date).format(format);
};

export const formatFromFilter = (fromFilterTemp: Dayjs) => fromFilterTemp.startOf('day').format(filterDateFormat);

export const formatToFilter = (toFilterTemp: Dayjs) => toFilterTemp.endOf('day').format(filterDateFormat);

export function toApiIsoUtc(value: Dayjs | Date | string | null | undefined): string | null {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.utc().toISOString() : null;
}

export function apiToLocalDayjs(src: string | null | undefined): Dayjs | null {
  if (!src) return null;
  if (src.includes('T')) {
    const d = dayjs.utc(src);
    return d.isValid() ? d.local() : null;
  }
  const isoish = src.replace(' ', 'T') + 'Z';
  const d = dayjs.utc(isoish);
  return d.isValid() ? d.local() : null;
}

// ---------------------------------------------------------------------------
// API UTC (@fishven/utils) — use for licenses and new code; migrate callers
// from legacy helpers above into these over time.
// ---------------------------------------------------------------------------

/** Like {@link fromUTCStringToLocalString} but parses API strings as UTC via @fishven/utils. */
export const fromApiUtcStringToLocalFilterString = (dateUTC: string) => {
  if (!dateUTC?.trim()) {
    return '';
  }
  const trimmed = dateUTC.trim();
  const parsed = parseApiUtcDateTimeToLocal(trimmed);
  if (parsed) {
    return (parsed as Dayjs).format(filterDateFormat);
  }
  if (trimmed.includes('T')) {
    const iso = dayjs.utc(trimmed);
    return iso.isValid() ? iso.local().format(filterDateFormat) : '';
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const d = dayjs.utc(trimmed, 'YYYY-MM-DD', true);
    return d.isValid() ? d.local().format(filterDateFormat) : '';
  }
  return '';
};

/** Like {@link formatDateByLang} but interprets API UTC datetimes via @fishven/utils before formatting. */
export const formatDateByLangFromApiUtc = (
  dateStr: string,
  locale: string,
  dateFormat: IDateFormatByLang,
  withTime = false,
  dateFormatType: keyof (typeof dateFormat)[Language] = 'long',
  isReversed = false,
) => {
  if (!dateStr?.trim()) {
    return '';
  }
  const trimmed = dateStr.trim();

  const targetDateFormat = dateFormat?.[locale]?.[dateFormatType] || 'DD/MM/YYYY';
  const outFormat = withTime ? ensureDateTimeFormat(targetDateFormat) : targetDateFormat;

  const fromApiUtc = parseApiUtcDateTimeToLocal(trimmed);
  if (fromApiUtc) {
    return (fromApiUtc as Dayjs).format(outFormat);
  }

  if (trimmed.includes('T')) {
    const iso = dayjs.utc(trimmed);
    if (iso.isValid()) {
      return iso.local().format(outFormat);
    }
  }

  const dateOnly = dayjs.utc(trimmed, 'YYYY-MM-DD', true);
  if (dateOnly.isValid()) {
    return dateOnly.local().format(outFormat);
  }

  const asLocalWire = dayjs(trimmed, filterDateFormat, true);
  if (asLocalWire.isValid()) {
    return asLocalWire.format(outFormat);
  }

  const loose = dayjs(trimmed);
  if (loose.isValid()) {
    return loose.format(outFormat);
  }

  const params = trimmed.split('-');
  const days = isReversed ? params[2] : params[0];
  const months = isReversed ? params[1] : params[1];
  const year = isReversed ? params[0] : params[2];
  const yearToDisplay = year ? year : String(new Date().getFullYear() - 1);
  const date = `${days}-${months}-${yearToDisplay}`;
  return dayjs(date).format(outFormat);
};

/** Like {@link apiToLocalDayjs} but prefers @fishven/utils parsing for API-shaped strings. */
export function apiUtcStringToLocalDayjs(src: string | null | undefined): Dayjs | null {
  if (!src) return null;
  const trimmed = src.trim();
  const fromPkg = parseApiUtcDateTimeToLocal(trimmed);
  if (fromPkg) return fromPkg as Dayjs;
  if (trimmed.includes('T')) {
    const d = dayjs.utc(trimmed);
    return d.isValid() ? d.local() : null;
  }
  const isoish = trimmed.replace(' ', 'T') + 'Z';
  const d = dayjs.utc(isoish);
  return d.isValid() ? d.local() : null;
}
